import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCalendar } from './hooks/useCalendar';
import useBookingAvailability from './hooks/useBookingAvailability';
import SingleDayStrategy from './strategies/SingleDayStrategy';
import MultiDayFixedStrategy from './strategies/MultiDayFixedStrategy';

/**
 * BookingCalendar Component
 *
 * Modern booking calendar built on the headless calendar architecture.
 * Separates logic (hooks + strategy) from UI for better maintainability.
 *
 * Features:
 * - Single-day and multi-day fixed selection for bookings
 * - Strategy injection based on experience.duration_type
 * - Real-time availability from API
 * - 6 availability states with visual indicators
 * - Range highlighting for multi-day trips (rose-600/rose-100)
 * - Hover preview of trip range on valid start dates
 * - Intelligent caching (5-month + 2-month buffer)
 * - Hover tooltips (desktop only)
 * - Dynamic legend (only shows encountered states)
 * - Full accessibility (ARIA labels, keyboard focus)
 *
 * Part of: Booking Calendar Migration (Phase 2)
 * Updated: MVP 1.4.2 SCRUM-408 - Multi-day strategy injection
 * Location: apps/web/src/components/calendar/BookingCalendar.jsx
 * Created: 2025-11-01
 *
 * @param {Object} props - Component props
 * @param {string} props.guideId - Guide UUID
 * @param {string} props.guideServiceId - Guide service UUID
 * @param {Date} props.selectedDate - Currently selected date
 * @param {Function} props.onDateSelect - Callback when single date is selected (receives Date)
 * @param {Function} [props.onDatesSelect] - Callback when multiple dates selected (receives Date[])
 * @param {Date} props.initialMonth - Optional initial month to display
 * @param {Object} [props.experience] - Experience config for strategy selection
 * @param {Object} [props.strategy] - Optional pre-built strategy override
 */
export default function BookingCalendar({
  guideId,
  guideServiceId,
  selectedDate,
  onDateSelect,
  onDatesSelect,
  initialMonth,
  experience,
  strategy: strategyOverride
}) {
  // Hover state for tooltips and range preview
  const [hoveredDate, setHoveredDate] = useState(null);

  // Initialize strategy based on experience type or use override
  const bookingLeadTimeHours = experience?.bookingLeadTimeHours || experience?.booking_lead_time_hours || 24;
  const strategy = useMemo(() => {
    if (strategyOverride) {
      return strategyOverride;
    }
    if (experience?.duration_type === 'fixed_multi_day') {
      return new MultiDayFixedStrategy({
        durationDays: experience.duration_days || 1,
        prepDays: experience.prep_days || 0,
        cleanupDays: experience.cleanup_days || 0,
        startDayRestrictions: experience.start_day_restrictions || null,
        minBookingHours: bookingLeadTimeHours,
        bookingHorizonMonths: 12
      });
    }
    return new SingleDayStrategy({
      minBookingHours: bookingLeadTimeHours,
      bookingHorizonMonths: 12,
      startDayRestrictions: experience?.start_day_restrictions || null
    });
  }, [strategyOverride, experience?.duration_type, experience?.duration_days,
      experience?.prep_days, experience?.cleanup_days, experience?.start_day_restrictions,
      bookingLeadTimeHours]);

  // Get booking horizon constraints from strategy
  const minDate = useMemo(() => strategy.getMinBookingDate(), [strategy]);
  const maxDate = useMemo(() => strategy.getMaxBookingDate(), [strategy]);

  // Determine if this is a multi-day strategy
  const isMultiDay = strategy instanceof MultiDayFixedStrategy;

  // Initialize calendar state with constraints
  const calendar = useCalendar({
    strategy,
    initialMonth: initialMonth || getDefaultInitialMonth(selectedDate),
    minDate,
    maxDate,
    onSelectionComplete: (dates) => {
      if (dates.length === 1 && onDateSelect) {
        if (isMultiDay) {
          const endDate = strategy._getEndDate(dates[0]);
          onDateSelect(dates[0], endDate);
        } else {
          onDateSelect(dates[0]);
        }
      } else if (dates.length > 1 && onDatesSelect) {
        onDatesSelect(dates);
      }
    }
  });

  // Load availability data with caching
  const availability = useBookingAvailability({
    guideId,
    guideServiceId,
    currentMonth: calendar.currentMonth
  });

  /**
   * Combine calendar date availability with API availability data
   * Calendar provides basic date validation, availability hook provides API data
   */
  const isDateFullyAvailable = (date) => {
    return calendar.isDateAvailable(date) && availability.isDateAvailable(date);
  };

  /**
   * Format month and year for display
   */
  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  /**
   * Get full date label for accessibility
   */
  const getDateLabel = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  /**
   * Get availability label for accessibility
   */
  const getAvailabilityLabel = (date) => {
    if (availability.isDatePast(date)) {
      return 'Past date, not available';
    }
    if (availability.isDateTooSoon(date)) {
      return 'Not enough advance notice (minimum 48 hours required)';
    }
    if (availability.isDateNotYetAvailable(date)) {
      return 'Not yet available for booking (beyond booking horizon)';
    }
    if (!isDateFullyAvailable(date)) {
      return 'Fully booked';
    }
    if (availability.isDateLimited(date)) {
      return 'Limited availability';
    }
    return 'Available';
  };

  /**
   * Check if a date is the start of the selected multi-day trip
   */
  const isStartDate = (date) => {
    return isMultiDay && selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  /**
   * Check if a date is the end of the selected multi-day trip
   */
  const isEndDate = (date) => {
    if (!isMultiDay || !selectedDate) {
      return false;
    }
    const endDate = strategy._getEndDate(selectedDate);
    return date.toDateString() === endDate.toDateString();
  };

  /**
   * Check if a date is within the hover preview range (multi-day)
   */
  const isInHoverRange = (date) => {
    if (!isMultiDay || !hoveredDate || selectedDate) {
      return false;
    }
    return strategy.isDateInRange(date, [hoveredDate]) && strategy.isDateBookable(hoveredDate);
  };

  /**
   * Get CSS classes for date styling based on state
   */
  const getDateClassName = (date) => {
    const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
    const isPast = availability.isDatePast(date);
    const isTooSoon = availability.isDateTooSoon(date);
    const isNotYetAvailable = availability.isDateNotYetAvailable(date);
    const isAvailable = isDateFullyAvailable(date);
    const isLimited = availability.isDateLimited(date);
    const isInCurrentMonth = calendar.isDateInCurrentMonth(date);
    const inRange = isMultiDay && selectedDate && strategy.isDateInRange(date, [selectedDate]);
    const isStart = isStartDate(date);
    const isEnd = isEndDate(date);
    const inHoverPreview = isInHoverRange(date);

    let classes = 'aspect-square p-1 text-sm rounded-lg transition relative w-full ';

    if (isPast || isTooSoon) {
      classes += 'text-gray-300 cursor-not-allowed';
    } else if (isNotYetAvailable) {
      classes += 'text-gray-400 cursor-not-allowed border border-gray-200 line-through';
    } else if (!isAvailable && !inRange) {
      classes += 'text-gray-400 line-through cursor-not-allowed';
    } else if (isStart || isEnd) {
      // Multi-day start/end markers
      classes += 'bg-rose-600 text-white font-semibold rounded-full';
    } else if (isSelected && !isMultiDay) {
      // Single-day selected
      classes += 'bg-rose-600 text-white font-semibold';
    } else if (inRange) {
      // Multi-day trip day fill
      classes += 'bg-rose-100 text-rose-900';
    } else if (inHoverPreview) {
      // Hover preview for multi-day
      classes += 'bg-rose-50 text-gray-900';
    } else if (isLimited) {
      classes += 'bg-amber-50 text-amber-700 hover:bg-amber-100';
    } else {
      classes += 'hover:bg-rose-50 text-gray-900';
    }

    // Dim dates from other months
    if (!isInCurrentMonth) {
      classes += ' opacity-40';
    }

    return classes;
  };

  /**
   * Render checkerboard background for "not yet available" dates
   */
  const getCheckerboardStyle = (date) => {
    if (availability.isDateNotYetAvailable(date)) {
      return {
        backgroundImage: 'repeating-linear-gradient(45deg, #f3f4f6 0, #f3f4f6 2px, transparent 2px, transparent 4px)',
        backgroundSize: '5.66px 5.66px'
      };
    }
    return {};
  };

  /**
   * Render tooltip on hover (desktop only)
   */
  const renderTooltip = (date) => {
    const dateInfo = availability.getDateInfo(date);
    const isAvailable = isDateFullyAvailable(date);
    const isLimited = availability.isDateLimited(date);

    if (!hoveredDate || hoveredDate.toDateString() !== date.toDateString()) {
      return null;
    }

    if (!dateInfo || (!isAvailable && !isLimited)) {
      return null;
    }

    return (
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20 hidden md:block">
        <div className="bg-gray-900 text-white text-xs rounded-lg shadow-lg p-2 whitespace-nowrap">
          <div className="font-semibold mb-1">
            {date.toLocaleDateString('en-US', { weekday: 'long' })}
          </div>
          {dateInfo.slots_available > 0 && (
            <div className="text-gray-300">
              {dateInfo.slots_available <= 3
                ? `${dateInfo.slots_available} ${dateInfo.slots_available === 1 ? 'spot' : 'spots'} remaining`
                : 'Available'}
            </div>
          )}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-4 border-transparent border-t-gray-900" />
        </div>
      </div>
    );
  };

  /**
   * Render dynamic legend showing only encountered states
   */
  const renderLegend = () => {
    const encounteredStates = availability.getEncounteredStates();

    return (
      <div className="mt-4 pt-4 border-t space-y-2 text-xs">
        {encounteredStates.has('available') && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-rose-50 border border-rose-200 rounded" />
            <span className="text-gray-600">Available</span>
          </div>
        )}
        {encounteredStates.has('limited') && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-amber-50 border border-amber-200 rounded relative">
              <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-amber-500 rounded-full" />
            </div>
            <span className="text-gray-600">Limited spots (1-3 left)</span>
          </div>
        )}
        {encounteredStates.has('not_yet_available') && (
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 border border-gray-200 rounded"
              style={{
                backgroundImage: 'repeating-linear-gradient(45deg, #f3f4f6 0, #f3f4f6 2px, transparent 2px, transparent 4px)',
                backgroundSize: '5.66px 5.66px'
              }}
            />
            <span className="text-gray-600">Not yet available</span>
          </div>
        )}
        {encounteredStates.has('fully_booked') && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs line-through">
              5
            </div>
            <span className="text-gray-600">Fully booked</span>
          </div>
        )}
        {selectedDate && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-rose-600 rounded" />
            <span className="text-gray-600">{isMultiDay ? 'Start / End date' : 'Selected'}</span>
          </div>
        )}
        {isMultiDay && selectedDate && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-rose-100 border border-rose-200 rounded" />
            <span className="text-gray-600">Trip days</span>
          </div>
        )}
      </div>
    );
  };

  // Loading state
  if (availability.loading && Object.keys(availability.availability).length === 0) {
    return (
      <div className="mt-4 pt-4 border-t" role="region" aria-label="Calendar" aria-busy="true">
        <div className="flex items-center justify-between mb-4">
          <button className="p-1 rounded opacity-40 cursor-not-allowed" disabled aria-label="Previous month (loading)">
            <ChevronLeft className="w-5 h-5" aria-hidden="true" />
          </button>
          <span className="font-semibold">{formatMonthYear(calendar.currentMonth)}</span>
          <button className="p-1 rounded opacity-40 cursor-not-allowed" disabled aria-label="Next month (loading)">
            <ChevronRight className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* Skeleton loading state */}
        <div className="grid grid-cols-7 gap-1">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
              {day}
            </div>
          ))}
          {Array.from({ length: 35 }).map((_, i) => (
            <div
              key={`skeleton-${i}`}
              className="aspect-square p-1 text-sm rounded-lg bg-gray-100 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  // Main calendar render
  return (
    <div className="mt-4 pt-4 border-t" role="region" aria-label="Booking calendar">
      {/* Trip duration display for multi-day */}
      {isMultiDay && experience && (
        <div className="text-lg font-semibold text-neutral-900 mb-3">
          {experience.duration_days}-Day Trip
        </div>
      )}

      {/* Selected date range display for multi-day */}
      {isMultiDay && selectedDate && (
        <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-800">
          {strategy.getSelectionDisplay([selectedDate])}
        </div>
      )}

      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="relative group">
          <button
            onClick={calendar.navigateToPrevMonth}
            className={`p-1 rounded ${!calendar.canGoPrevious() ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100'}`}
            disabled={!calendar.canGoPrevious()}
            title={!calendar.canGoPrevious() ? "Cannot view past months" : "Previous month"}
            aria-label={!calendar.canGoPrevious() ? "Cannot view past months" : "Previous month"}
          >
            <ChevronLeft className="w-5 h-5" aria-hidden="true" />
          </button>
          {!calendar.canGoPrevious() && (
            <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-10 w-40 p-2 text-xs bg-gray-900 text-white rounded shadow-lg">
              Cannot view past months
            </div>
          )}
        </div>

        <span className="font-semibold" aria-live="polite" aria-atomic="true">
          {formatMonthYear(calendar.currentMonth)}
        </span>

        <div className="relative group">
          <button
            onClick={calendar.navigateToNextMonth}
            className={`p-1 rounded ${!calendar.canGoNext() ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100'}`}
            disabled={!calendar.canGoNext()}
            title={!calendar.canGoNext() ? "Booking horizon reached (12 months)" : "Next month"}
            aria-label={!calendar.canGoNext() ? "Booking horizon reached (12 months)" : "Next month"}
          >
            <ChevronRight className="w-5 h-5" aria-hidden="true" />
          </button>
          {!calendar.canGoNext() && (
            <div className="absolute right-0 top-full mt-1 hidden group-hover:block z-10 w-48 p-2 text-xs bg-gray-900 text-white rounded shadow-lg">
              Booking horizon reached (12 months)
            </div>
          )}
        </div>
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1" role="grid" aria-label="Calendar dates">
        {/* Weekday labels */}
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
          <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2" role="columnheader">
            {day}
          </div>
        ))}

        {/* Date buttons */}
        {calendar.daysInMonth.map((date, idx) => {
          const isAvailable = isDateFullyAvailable(date);
          const isPast = availability.isDatePast(date);
          const isTooSoon = availability.isDateTooSoon(date);
          const isNotYetAvailable = availability.isDateNotYetAvailable(date);
          const isLimited = availability.isDateLimited(date);
          const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();

          return (
            <div key={idx} className="relative">
              <button
                disabled={!isAvailable || isPast || isTooSoon || isNotYetAvailable}
                onClick={() => {
                  if (isAvailable && !isPast && !isTooSoon && !isNotYetAvailable) {
                    calendar.selectDate(date);
                  }
                }}
                onMouseEnter={() => setHoveredDate(date)}
                onMouseLeave={() => setHoveredDate(null)}
                style={getCheckerboardStyle(date)}
                role="gridcell"
                aria-label={`${getDateLabel(date)}, ${getAvailabilityLabel(date)}`}
                aria-selected={isSelected}
                aria-disabled={!isAvailable || isPast || isTooSoon || isNotYetAvailable}
                tabIndex={isSelected ? 0 : -1}
                className={getDateClassName(date)}
              >
                {date.getDate()}

                {/* Limited availability indicator dot */}
                {isLimited && isAvailable && !isSelected && (
                  <div
                    className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-amber-500 rounded-full"
                    aria-hidden="true"
                  />
                )}
              </button>

              {/* Hover tooltip */}
              {renderTooltip(date)}
            </div>
          );
        })}
      </div>

      {/* Dynamic legend */}
      {renderLegend()}
    </div>
  );
}

/**
 * Get default initial month for calendar
 * If selected date exists, use that month
 * Otherwise, use current month UNLESS we're near the end of the month
 * and most days would be unavailable due to the 48-hour advance notice
 */
function getDefaultInitialMonth(selectedDate) {
  if (selectedDate) {
    return new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
  }

  const today = new Date();
  const currentDay = today.getDate();
  const daysInCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

  // Calculate first available booking date (default 24 hours from now)
  const minBookingDate = new Date();
  minBookingDate.setHours(minBookingDate.getHours() + 24);

  // Check if the first available date is in the next month
  const isMinDateNextMonth = minBookingDate.getMonth() !== today.getMonth();

  // If we're in the last 3 days of the month OR the minimum booking date is already next month,
  // default to next month. Otherwise show current month.
  if (currentDay >= daysInCurrentMonth - 2 || isMinDateNextMonth) {
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    nextMonth.setDate(1);
    return nextMonth;
  }

  // Default to current month
  return new Date(today.getFullYear(), today.getMonth(), 1);
}
